import { modeStatus, practiceModel } from "@aybuben/client/screens/practice.ts";
import type { PracticeMode } from "@aybuben/core/practice.ts";
import type { GameState } from "@aybuben/core/state.ts";
import {
	CaseSensitive,
	Headphones,
	MessagesSquare,
	PenLine,
	RotateCcw,
	Shuffle,
	type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { useCourse } from "../content/context.tsx";
import { useStrings } from "../i18n/index.tsx";
import { COL, FOCUS } from "../theme.ts";
import { Mascot } from "./Mascot.tsx";
import { Tree } from "./tree/index.tsx";

const MODE_ICON: Record<PracticeMode, LucideIcon> = {
	mix: Shuffle,
	mistakes: RotateCcw,
	letters: CaseSensitive,
	words: MessagesSquare,
	listen: Headphones,
	write: PenLine,
};

export { modeStatus };

interface PracticeProps {
	st: GameState;
	onStart: (mode: PracticeMode) => void;
	/** Starts the shortest round that brings the tree's summer back. */
	onWater?: () => void;
	/** The learner's tree shape (`tree/seeds.ts`). */
	seed?: number;
}

export function Practice({ st, onStart, onWater, seed }: PracticeProps) {
	const course = useCourse();
	const t = useStrings();
	// Render must stay pure, so the counts read a clock kept in state. A round
	// replaces the tab and remounts it, which refreshes it after each one; the
	// tab can also sit open for hours, so returning to the page refreshes it too.
	const [now, setNow] = useState(Date.now);
	useEffect(() => {
		const wake = () => {
			if (document.visibilityState === "visible") setNow(Date.now());
		};
		document.addEventListener("visibilitychange", wake);
		return () => document.removeEventListener("visibilitychange", wake);
	}, []);
	const { empty, subtitle, toSummer, tree, cards } = useMemo(
		() => practiceModel(st, course, now),
		[st, course, now],
	);
	return (
		<div className="px-4 pt-5 pb-32">
			<h2 className="text-2xl font-extrabold">{t("practice.title")}</h2>
			<p className="mt-1 text-sm" style={{ color: COL.mute }}>
				{t(subtitle)}
			</p>
			{empty && (
				<div className="mt-6 flex justify-center">
					<Mascot state="idle" size={96} />
				</div>
			)}
			{onWater && toSummer > 0 && (
				<button
					type="button"
					onClick={() => {
						onWater();
						setNow(Date.now());
					}}
					data-testid="water-card"
					className={`mt-4 flex w-full items-center gap-3 rounded-3xl p-4 text-left transition active:translate-y-0.5 ${FOCUS}`}
					style={{
						background: COL.greenSoft,
						border: `2px solid ${COL.green}`,
						boxShadow: `0 4px 0 ${COL.green}`,
					}}
				>
					<div className="h-12 w-12 shrink-0">
						<Tree tree={tree} variant="icon" seed={seed} decorative />
					</div>
					<div className="flex-1">
						<div className="font-extrabold" style={{ color: COL.greenDark }}>
							{t("tree.water")}
						</div>
						<div className="text-sm" style={{ color: COL.mute }}>
							{t("practice.waterBody")}
						</div>
						<div className="mt-1 text-xs font-semibold" style={{ color: COL.greenDark }}>
							{t("tree.goalSummer", { n: toSummer })}
						</div>
					</div>
				</button>
			)}
			{!empty && (
				<div className="mt-4 flex flex-col gap-3">
					{cards.map(({ mode, title, body, status }) => {
						const Icon = MODE_ICON[mode];
						const on = status.enabled;
						return (
							<button
								key={mode}
								type="button"
								disabled={!on}
								onClick={() => {
									onStart(mode);
									// A tap that opened nothing (a mistake aged out since the counts
									// were read) must not leave a card that keeps opening nothing.
									setNow(Date.now());
								}}
								className={`flex w-full items-center gap-3 rounded-3xl p-4 text-left transition ${on ? "active:translate-y-0.5" : ""} ${FOCUS}`}
								style={{
									background: on ? COL.apricotSoft : COL.card,
									border: `2px solid ${on ? COL.apricotLine : COL.line}`,
									boxShadow: on ? `0 4px 0 ${COL.apricotLine}` : "none",
									opacity: on ? 1 : 0.6,
								}}
							>
								<Icon color={on ? COL.apricotDark : COL.greyDark} aria-hidden />
								<div className="flex-1">
									<div className="font-extrabold">{t(title)}</div>
									<div className="text-sm" style={{ color: COL.mute }}>
										{t(body)}
									</div>
									<div
										className="mt-1 text-xs font-semibold"
										style={{ color: on ? COL.apricotDark : COL.mute }}
									>
										{status.n === undefined ? t(status.line) : t(status.line, { n: status.n })}
									</div>
								</div>
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
}
